import { lazy, type ComponentType } from 'react';
import type { ViewRegistry } from '@/app/routing/view-registry';
import { useNavigation } from '@/app/navigation/use-navigation';

const RequisicaoAtiva = lazy(() => import('@/features/suprimentos/components/RequisicaoAtiva'));
const RequisicaoEncerrada = lazy(() => import('@/features/suprimentos/components/RequisicaoEncerrada'));
const MovimentacaoEstoque = lazy(
  () => import('@/features/suprimentos/components/MovimentacaoEstoque'),
);
const BalancoEstoque = lazy(() => import('@/features/suprimentos/components/BalancoEstoque'));
const Cat = lazy(() => import('@/features/suprimentos/components/Cat'));
const TriagemCompra = lazy(() => import('@/features/suprimentos/components/TriagemCompra'));
const OrdemCompra = lazy(() => import('@/features/suprimentos/components/OrdemCompra'));
const OrdemCompraStatus = lazy(() => import('@/features/suprimentos/components/OrdemCompraStatus'));
const Marca = lazy(() => import('@/features/suprimentos/components/Marca'));
const ProdutoCategoria = lazy(() => import('@/features/suprimentos/components/ProdutoCategoria'));
const ProdutoSubCategoria = lazy(
  () => import('@/features/suprimentos/components/ProdutoSubCategoria'),
);
const ProdutoTipo = lazy(() => import('@/features/suprimentos/components/ProdutoTipo'));
const ProdutoUnidadeMedida = lazy(
  () => import('@/features/suprimentos/components/ProdutoUnidadeMedida'),
);
const ProdutoMargemTipo = lazy(() => import('@/features/suprimentos/components/ProdutoMargemTipo'));
const ProdutoClassificacao = lazy(
  () => import('@/features/suprimentos/components/ProdutoClassificacao'),
);

function makeOnBackOnly(Component: ComponentType<{ onBack: () => void }>) {
  return function View() {
    const { actions } = useNavigation();
    return <Component onBack={actions.goBack} />;
  };
}

function RequisicaoAtivaView() {
  const { requisicaoNotificacaoId, autoOpenSeparacao, actions } = useNavigation();
  return (
    <RequisicaoAtiva
      onBack={actions.goBack}
      initialOpenRequisicaoId={requisicaoNotificacaoId}
      onClearInitialOpen={() => actions.setRequisicaoNotification(null, autoOpenSeparacao)}
      autoOpenSeparacao={autoOpenSeparacao}
      onAutoOpenSeparacaoConsumed={() =>
        actions.setRequisicaoNotification(requisicaoNotificacaoId, false)
      }
    />
  );
}

export const suprimentosViews: Partial<ViewRegistry> = {
  'suprimentos-requisicoes-ativas': {
    component: RequisicaoAtivaView,
    guard: { permission: 'REQUISICAO' },
  },
  'suprimentos-requisicoes-encerradas': {
    component: makeOnBackOnly(RequisicaoEncerrada),
    guard: { permission: 'REQUISICAO' },
  },
  'suprimentos-movimentacao': {
    component: makeOnBackOnly(MovimentacaoEstoque),
    guard: { permission: 'MOVIMENTACAO_ESTOQUE' },
  },
  'suprimentos-balanco': {
    component: makeOnBackOnly(BalancoEstoque),
    guard: { permission: 'ESTOQUE' },
  },
  'suprimentos-cat': { component: makeOnBackOnly(Cat), guard: { permission: 'CAT' } },
  'suprimentos-triagem': {
    component: makeOnBackOnly(TriagemCompra),
    guard: { permission: 'ORDEM_COMPRA' },
  },
  'suprimentos-ordem-compra': {
    component: makeOnBackOnly(OrdemCompra),
    guard: { permission: 'ORDEM_COMPRA' },
  },
  'suprimentos-ordem-compra-status': {
    component: makeOnBackOnly(OrdemCompraStatus),
    guard: { permission: 'ORDEM_COMPRA_STATUS' },
  },
  'suprimentos-marca': { component: makeOnBackOnly(Marca), guard: { permission: 'MARCA' } },
  'suprimentos-produto-categoria': {
    component: makeOnBackOnly(ProdutoCategoria),
    guard: { permission: 'PRODUTO_CATEGORIA' },
  },
  'suprimentos-produto-subcategoria': {
    component: makeOnBackOnly(ProdutoSubCategoria),
    guard: { permission: 'PRODUTO_SUBCATEGORIA' },
  },
  'suprimentos-produto-tipo': {
    component: makeOnBackOnly(ProdutoTipo),
    guard: { permission: 'PRODUTO_TIPO' },
  },
  'suprimentos-unidade-medida': {
    component: makeOnBackOnly(ProdutoUnidadeMedida),
    guard: { permission: 'PRODUTO_UNIDADE' },
  },
  'suprimentos-margem-tipo': {
    component: makeOnBackOnly(ProdutoMargemTipo),
    guard: { permission: 'PRODUTO_MARGEM' },
  },
  'suprimentos-classificacao': {
    component: makeOnBackOnly(ProdutoClassificacao),
    guard: { permission: 'PRODUTO' },
  },
};
